// This is an exact copy of the sanitization code from the BFF at src/utils/sanitization.ts

import {decodeHTML} from 'entities';
import sanitizeHtml from 'sanitize-html';

/**
 * Strict sanitization options that remove all HTML tags and dangerous content
 * This configuration is extremely restrictive and will reject any HTML, scripts,
 * event handlers, and dangerous protocols
 */
const STRICT_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [], // No HTML tags allowed at all
  allowedAttributes: {}, // No attributes allowed
  disallowedTagsMode: 'discard', // Remove disallowed tags completely
  allowProtocolRelative: false, // Block protocol-relative URLs
  allowedSchemes: [], // Block all URL schemes (including javascript:, data:, etc.)
  allowedSchemesByTag: {}, // No schemes allowed for any tag
  allowedSchemesAppliedToAttributes: ['href', 'src', 'cite'], // Apply scheme restrictions to these attributes
  enforceHtmlBoundary: false,
};

/**
 * Simple regex patterns for text-based threats that sanitize-html doesn't catch
 * These are kept minimal and only cover the most critical injection vectors
 */
const TEXT_THREAT_PATTERNS = [
  /javascript:/i,
  /vbscript:/i,
  /data:text\/html/i,
  /on\w+\s*=/i, // Event handlers like onclick=, onerror=
];

/**
 * Checks if a string contains potentially dangerous script patterns
 * by comparing the original string with its sanitized version
 * @param value - The string to check
 * @returns true if dangerous patterns are detected, false otherwise
 */
export const containsDangerousScript = (value: string): boolean => {
  if (TEXT_THREAT_PATTERNS.some(pattern => pattern.test(value))) {
    return true;
  }

  const sanitized = sanitizeHtml(value, STRICT_OPTIONS);
  // Decode HTML entities to avoid rejecting valid characters like "&" which become "&amp;"
  const decodedSanitized = decodeHTML(sanitized);
  return decodedSanitized !== value;
};

/**
 * Sanitizes a string by removing HTML tags and dangerous characters
 * This is a defensive measure - ideally validation should reject inputs before this point
 * @param value - The string to sanitize
 * @returns The sanitized string
 */
export const sanitizeString = (value: string): string => {
  // Use sanitize-html to remove all HTML tags and dangerous content
  let sanitized = sanitizeHtml(value, STRICT_OPTIONS);

  // Decode HTML entities to preserve valid characters like "&" which become "&amp;"
  sanitized = decodeHTML(sanitized);

  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, '');

  // Trim whitespace
  sanitized = sanitized.trim();

  return sanitized;
};

/**
 * Validates that a string does not contain script injection attempts
 * Throws an error if dangerous patterns are detected
 * @param value - The string to validate
 * @param fieldName - Optional field name for better error messages
 * @throws Error if dangerous patterns are detected
 */
export const validateNoScriptInjection = (value: string, fieldName = 'Input'): void => {
  if (containsDangerousScript(value)) {
    throw new Error(`${fieldName} contains potentially dangerous content and has been rejected for security reasons`);
  }
};
